import type { ReactNode } from 'react';
import type { Passo, TomDeAviso, TomSemantico } from '../../componentes/ui';
import type { DocumentoPontual, EtapaDaNegociacao, Negociacao } from './tipos';

/**
 * O que a tela da operação (`NegociacoesPontual`, `DetalheDaNegociacao`) e a
 * das partes (`PontualDaParte`) mostram igual: rótulos, tons e a trilha dos
 * quatro passos. Num lugar só para as duas não contarem a mesma negociação de
 * dois jeitos.
 */

export const ROTULO_DO_STATUS: Readonly<Record<string, string>> = {
  SIMULACAO: 'Simulação',
  FECHADA: 'Fechada',
  PROTOCOLADA: 'Protocolada',
  TRANSFERIDA: 'Transferida',
  PAGA: 'Paga',
  CANCELADA: 'Cancelada',
};

export const TOM_DO_STATUS: Readonly<Record<string, TomSemantico>> = {
  SIMULACAO: 'neutro',
  FECHADA: 'informativo',
  PROTOCOLADA: 'atencao',
  TRANSFERIDA: 'progresso',
  PAGA: 'positivo',
  CANCELADA: 'critico',
};

/** RN-704 — de quem é a bola, em uma palavra, para caber na linha. */
export const QUEM: Readonly<Record<string, string>> = {
  OPERACAO: 'nós',
  FORNECEDOR: 'fornecedor',
  COMPRADOR: 'comprador',
  ASSESSOR: 'assessor',
  RECEITA: 'Receita',
};

/** As etapas do ciclo, na ordem, com o nome que o filtro da lista mostra. */
export const ETAPAS: readonly { readonly chave: EtapaDaNegociacao; readonly rotulo: string }[] = [
  { chave: 'A_FECHAR', rotulo: 'Em simulação' },
  { chave: 'CONTRATO_EM_ASSINATURA', rotulo: 'Contrato em assinatura' },
  { chave: 'REQUERIMENTO', rotulo: 'Aguardando requerimento' },
  { chave: 'A_PROTOCOLAR', rotulo: 'A protocolar' },
  { chave: 'AGUARDANDO_DEFERIMENTO', rotulo: 'Aguardando deferimento' },
  { chave: 'A_RECEBER', rotulo: 'A receber' },
  { chave: 'EM_ATRASO', rotulo: 'Parcela em atraso' },
  { chave: 'PAGA', rotulo: 'Paga' },
  { chave: 'CANCELADA', rotulo: 'Cancelada' },
];

export const ROTULO_DO_TIPO: Readonly<Record<string, string>> = {
  EXPORTACAO: 'Exportação',
  DIFERIDO: 'Diferido',
  OUTRO: 'Outro',
};

export const TOM_DA_ACAO: Readonly<Record<string, TomDeAviso>> = {
  aguardando: 'informacao',
  atencao: 'atencao',
  concluido: 'positivo',
  neutro: 'informacao',
};

export const ROTULO_DO_DOCUMENTO: Readonly<Record<DocumentoPontual['tipo'], string>> = {
  CONTRATO: 'Contrato de cessão',
  REQUERIMENTO: 'Requerimento de transferência',
  CERTIFICADO: 'Certificado de transferência',
  OUTRO: 'Outro documento',
  TERMO_COMISSIONAMENTO: 'Termo de comissionamento',
};

/**
 * RN-704 — Os quatro passos, com o que já aconteceu datado.
 *
 * A trilha é derivada do estado, não guardada: o que define "protocolado" é ter
 * número de protocolo, e "transferido" é ter data de deferimento. Um campo à
 * parte dizendo em que passo a negociação está poderia divergir do fato.
 */
export function passosDa(
  n: Pick<
    Negociacao,
    | 'status'
    | 'protocoloTransferencia'
    | 'requerimentoAssinadoEm'
    | 'dataTransferencia'
    | 'parcelasPagamento'
    | 'regraPagamentoDescricao'
  >,
): readonly Passo[] {
  const cancelada = n.status === 'CANCELADA';
  const fechada = n.status !== 'SIMULACAO' && !cancelada;
  const protocolada = n.protocoloTransferencia !== null;
  const transferida = n.dataTransferencia !== null;
  const paga = n.status === 'PAGA';

  const estado = (concluido: boolean, atual: boolean): Passo['estado'] =>
    concluido ? 'concluido' : cancelada ? 'bloqueado' : atual ? 'atual' : 'pendente';

  const pendentes = n.parcelasPagamento.filter(
    (p) => p.status !== 'PAGA' && p.status !== 'CANCELADA',
  ).length;

  return [
    {
      id: 'negociacao',
      rotulo: 'Negociação contratual',
      descricao: fechada
        ? 'Contrato assinado'
        : 'Valores, forma de pagamento e contrato para assinatura',
      estado: estado(fechada, !fechada),
    },
    {
      id: 'protocolo',
      rotulo: 'Requerimento e protocolo',
      descricao: protocolada
        ? `Protocolo ${n.protocoloTransferencia ?? ''}`
        : n.requerimentoAssinadoEm !== null
          ? 'Requerimento assinado'
          : 'Requerimento assinado e protocolado na Receita',
      estado: estado(protocolada, fechada && !protocolada),
    },
    {
      id: 'transferencia',
      rotulo: 'Transferência deferida',
      descricao: transferida
        ? `Deferida em ${emBr(n.dataTransferencia ?? '')}`
        : 'Certificado da Receita — inicia o prazo de pagamento',
      estado: estado(transferida, protocolada && !transferida),
    },
    {
      id: 'pagamento',
      rotulo: 'Pagamento',
      descricao: paga
        ? 'Comprador e honorários quitados'
        : transferida
          ? `${pendentes} parcela(s) em aberto`
          : (n.regraPagamentoDescricao ?? 'Conforme o combinado no contrato'),
      estado: estado(paga, transferida && !paga),
    },
  ];
}

export function emBr(iso: string): string {
  if (iso === '') return '—';
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

export function Item(props: {
  readonly rotulo: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-conteudo-sutil">{props.rotulo}</dt>
      <dd>{props.children}</dd>
    </div>
  );
}
